import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import debounce from "lodash/debounce";
import { toast } from "react-toastify";

const translitMap = {
  q: "й", w: "ц", e: "у", r: "к", t: "е", y: "н", u: "г", i: "ш", o: "щ", p: "з", "[": "х", "]": "ъ",
  a: "ф", s: "ы", d: "в", f: "а", g: "п", h: "р", j: "о", k: "л", l: "д", ";": "ж", "'": "э",
  z: "я", x: "ч", c: "с", v: "м", b: "и", n: "т", m: "ь", ",": "б", ".": "ю", "/": "."
};

const latinTranslitMap = {
  'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'yo', 'ж': 'zh', 'з': 'z',
  'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm', 'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r',
  'с': 's', 'т': 't', 'у': 'u', 'ф': 'f', 'х': 'kh', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'sch',
  'ъ': '', 'ы': 'y', 'ь': '', 'э': 'e', 'ю': 'yu', 'я': 'ya',
  // Специальные случаи для брендов и названий
  'скандик': 'skandik', 'профиплекс': 'profiplex', 'норд': 'nord', 'карбон': 'carbon',
  'лайн': 'line', 'лайф': 'life', 'профиль': 'profile', 'аквастатик': 'aquastatik',
  'бонолит': 'bonolit'
};

// Обратная транслитерация - из латиницы в кириллицу
const reverseLatinTranslitMap = {
  'a': 'а', 'b': 'б', 'v': 'в', 'g': 'г', 'd': 'д', 'e': 'е', 'yo': 'ё', 'zh': 'ж', 'z': 'з',
  'i': 'и', 'y': 'й', 'k': 'к', 'l': 'л', 'm': 'м', 'n': 'н', 'o': 'о', 'p': 'п', 'r': 'р',
  's': 'с', 't': 'т', 'u': 'у', 'f': 'ф', 'kh': 'х', 'ts': 'ц', 'ch': 'ч', 'sh': 'ш', 'sch': 'щ',
  'yu': 'ю', 'ya': 'я',
  // Дополнительные латинские буквы
  'c': 'к', 'h': 'х', 'j': 'дж', 'q': 'к', 'w': 'в', 'x': 'кс',
  // Специальные случаи
  'nord': 'норд', 'profiplex': 'профиплекс', 'skandik': 'скандик',
  'carbon': 'карбон', 'profile': 'профиль', 'line': 'лайн', 'aquastatik': 'аквастатик',
  'bonolit': 'бонолит'
};

const transliterate = (input) => {
  if (!input) return '';
  return input
    .split("")
    .map((char) => translitMap[char.toLowerCase()] || char)
    .join("");
};

// Буквы, которые на экране выглядят одинаково в латинице и кириллице. В названиях
// номенклатуры алфавиты перемешаны: маркировка профлиста заведена латинской «C»
// («C8», «C20»), а встречается и русская («С21 0,50»), у «Н»/«H» в 1С вперемешку
// оба варианта, внутри одного слова попадаются кентавры вроде «C20А» и «МП20R».
// Набрать такое правильно нельзя ни в одной раскладке, поэтому перед сравнением
// и запрос, и название приводим к одному виду (Роман, 21.10.2026).
//
// Это НЕ транслитерация: «Technonicol» в «Технониколь» она не превращает, тем
// занимаются функции выше. Здесь только склейка одинаковых на вид символов.
const GOMOGLIFY = {
  a: 'а', c: 'с', e: 'е', o: 'о', p: 'р', x: 'х', y: 'у',
  k: 'к', m: 'м', t: 'т', b: 'в', h: 'н',
};

const odinakovyeBukvy = (input) => {
  if (!input) return '';
  return String(input).toLowerCase().split('').map((ch) => GOMOGLIFY[ch] || ch).join('');
};

const transliterateToLatin = (input) => {
  if (!input) return '';
  let result = input.toLowerCase();

  // Сначала заменяем специальные случаи (целые слова)
  result = result.replace(/скандик/g, 'skandik');
  result = result.replace(/профиплекс/g, 'profiplex');
  result = result.replace(/норд/g, 'nord');
  result = result.replace(/карбон/g, 'carbon');
  result = result.replace(/аквастатик/g, 'aquastatik');
  result = result.replace(/лайн/g, 'line');
  result = result.replace(/лайф/g, 'life');
  result = result.replace(/профиль/g, 'profile');
  result = result.replace(/бонолит/g, 'bonolit');

  // Обратные фонетические замены
  result = result.replace(/кв/g, 'qu'); // кв → qu
  result = result.replace(/кс/g, 'x');  // кс → x

  // Обратные специальные окончания
  result = result.replace(/лит$/g, 'lit'); // лит в конце слова → lit

  // Обработка "ай" как "i" в определенных контекстах
  result = result.replace(/ай([бвгджзклмнпрстфхцчшщ])/g, 'i$1'); // ай перед согласными
  result = result.replace(/ай$/g, 'i'); // ай в конце слова

  // Затем обычная транслитерация
  return result.split('').map((char) => latinTranslitMap[char] || char).join('');
};


const transliterateToCyrillic = (input) => {
  if (!input) return '';
  let result = input.toLowerCase();

  // Сначала заменяем специальные случаи (целые слова и бренды)
  result = result.replace(/profiplex/g, 'профиплекс');
  result = result.replace(/skandik/g, 'скандик');
  result = result.replace(/carbon/g, 'карбон');
  result = result.replace(/nord/g, 'норд');
  result = result.replace(/aquastatik/g, 'аквастатик');

  // Случаи с "line", "life" и "lit"
  result = result.replace(/line/g, 'лайн');
  result = result.replace(/life/g, 'лайф');
  result = result.replace(/profile/g, 'профиль');
  result = result.replace(/bonolit/g, 'бонолит');

  // Фонетические особенности латинских сочетаний
  // "qu" всегда читается как "кв"
  result = result.replace(/qu/g, 'кв');

  // "ph" читается как "ф"
  result = result.replace(/ph/g, 'ф');

  // "th" читается как "т" или "ф" (упрощаем до "т")
  result = result.replace(/th/g, 'т');

  // "tion" читается как "ция" или "шн"
  result = result.replace(/tion/g, 'шн');

  // "x" в начале слова читается как "з", иначе как "кс"
  result = result.replace(/^x/g, 'з');
  result = result.replace(/x/g, 'кс');

  // "w" читается как "в" или "у" (упрощаем до "в")
  result = result.replace(/w/g, 'в');

  // Специальные окончания
  result = result.replace(/lit$/g, 'лит'); // lit в конце слова → лит

  // Обработка "i" как "ай" в определенных контекстах
  // Если "i" в конце слова или перед согласными - может быть "ай"
  result = result.replace(/([a-z])i([bcdfghjklmnpqrstvwxyz])/g, '$1ай$2'); // i между согласными
  result = result.replace(/([a-z])i$/g, '$1ай'); // i в конце слова

  // Затем многобуквенные сочетания
  result = result.replace(/sch/g, 'щ');
  result = result.replace(/zh/g, 'ж');
  result = result.replace(/kh/g, 'х');
  result = result.replace(/ts/g, 'ц');
  result = result.replace(/ch/g, 'ч');
  result = result.replace(/sh/g, 'ш');
  result = result.replace(/yo/g, 'ё');
  result = result.replace(/yu/g, 'ю');
  result = result.replace(/ya/g, 'я');

  // И наконец однобуквенные
  return result.split('').map((char) => reverseLatinTranslitMap[char] || char).join('');
};

const normalizeOsbText = (text) => {
  if (!text) return { primary: '', alternate: '' };
  let normalized = text
    .toLowerCase()
    .replace(/(?<![а-яёa-z])(?:осб|осп|osb|jc,)(?![а-яёa-z])/gi, "osb$")
    .replace(/(?:[вбb]\s?)/gi, "бетон$")
    .replace(/(?:[мm]\s?)/gi, "бетон$")
    .replace(/пеноплекс|пеноплэкс/g, "пеноплэкс")
    // Нормализация д/d с числами
    .replace(/д(\d)/g, "d$1")  // д500 → d500
    .replace(/[()]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  const withDot = normalized.replace(/,/g, ".");
  const withComma = normalized.replace(/\./g, ",");

  return { primary: withDot, alternate: withComma };
};

const uniqueByLabel = (options) => {
  const seenLabels = new Set();
  return options.filter(option => {
    if (!seenLabels.has(option.label)) {
      seenLabels.add(option.label);
      return true;
    }
    console.log(`📌 Дубликат label удалён: ${option.label}`);
    return false;
  });
};

const highlightMatch = (text, query) => {
  if (!query || !text) return text;

  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const words = escapedQuery.split(/\s+/).filter(word => word.length > 0);
  const regex = new RegExp(`(${words.join('|')})`, 'gi');

  const parts = text.split(regex);

  return parts.map((part, index) => {
    const isMatch = words.some(word => part.toLowerCase() === word.toLowerCase());
    if (isMatch) {
      return (
        <span
          key={index}
          style={{
            background: 'rgba(31, 127, 209, 0.14)',
            color: '#005ca9',
            fontWeight: 700,
            borderRadius: '3px',
            padding: '0 2px',
          }}
        >
          {part}
        </span>
      );
    }
    return part;
  });
};
const parsePrice = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const normalized = String(value).replace(/\s/g, '').replace(',', '.');
  const price = parseFloat(normalized);
  return Number.isFinite(price) && price > 0 ? price : 0;
};

const getPriceForPaymentMethod = (item, paymentMethod) => (
  paymentMethod === 'cash'
    ? parsePrice(item.PRICE_CASH)
    : parsePrice(item.PRICE_NONCASH)
);

const sortByPriceAvailability = (a, b) => {
  const aHasPrice = a.priceValue > 0;
  const bHasPrice = b.priceValue > 0;

  if (aHasPrice !== bHasPrice) {
    return aHasPrice ? -1 : 1;
  }

  return 0;
};

const showMinPrice = (item) => {
  return (
    <p style={{
      color: '#7a8aa3',
      margin: '2px 0 0',
      fontSize: '11.5px',
      fontWeight: 500,
    }}>
      цена: {item.priceValue === 0
        ? 'по запросу'
        : <span>от <b style={{ color: '#020217', fontWeight: 700 }}>{Math.ceil(item.priceValue).toLocaleString('ru-RU')} ₽</b></span>
      }
    </p>
  );
}

const LIMIT = 50;

const ProductSearch = React.forwardRef(
  ({ id, options, selectedValue, onChange, onMouseEnter, onMouseLeave, onKeyDown, restrictToNonmetallic = false, excludeNonmetallic = false, paymentMethod }, ref) => {
    const [inputValue, setInputValue] = useState(selectedValue || '');
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [isOptionsOpen, setIsOptionsOpen] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const [portalNode, setPortalNode] = useState(null);
    const dropdownRef = useRef(null);
    const textareaRef = useRef(null);
    const searchCache = useRef(new Map());
    const latestSearchRef = useRef(null);

    // Очищаем кеш поиска при изменении paymentMethod
    useEffect(() => {
      searchCache.current.clear();
      console.log('🗑️ Кеш поиска очищен, paymentMethod изменился:', paymentMethod);
    }, [paymentMethod]);

    React.useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
      openDropdown: () => {
        setIsOptionsOpen(true);
        textareaRef.current?.focus();
        debouncedSearch(inputValue);
      },
      closeDropdown: () => setIsOptionsOpen(false),
      resetValue: (value) => {
        setInputValue(value || '');
        setIsOptionsOpen(false);
        console.log(`🔄 Сброшено значение ProductSearch до: ${value || ''}`);
      }
    }));

    useEffect(() => {
      const styleElement = document.createElement('style');
      styleElement.textContent = `
        @keyframes spin {
          0% { transform: translateY(-50%) rotate(0deg); }
          100% { transform: translateY(-50%) rotate(360deg); }
        }
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .dropdown-item:hover {
          background: linear-gradient(90deg, #e9ecef 0%, #d3d6db 50%, #e9ecef 100%);
          background-size: 200% 200%;
          animation: gradientShift 3s ease infinite;
        }
      `;
      document.head.appendChild(styleElement);
      return () => document.head.removeChild(styleElement);
    }, []);

    useEffect(() => {
      const node = document.createElement('div');
      document.body.appendChild(node);
      setPortalNode(node);
      return () => document.body.removeChild(node);
    }, []);

    const debouncedSearch = useMemo(
      () => debounce((value) => {
        if (!value.trim()) {
          let allKeys = Object.keys(options)
            .filter(key => options[key].CALCULATOR); // Исключаем элементы без категории

          // Применяем ограничения если необходимо
          if (restrictToNonmetallic) {
            allKeys = allKeys.filter(key => options[key].CALCULATOR === "Нерудные материалы");
          } else if (excludeNonmetallic) {
            allKeys = allKeys.filter(key => options[key].CALCULATOR !== "Нерудные материалы");
          }

          const allOptions = allKeys.map(key => {
            const priceValue = getPriceForPaymentMethod(options[key], paymentMethod);

            return {
              value: key,
              label: key,
              category: options[key].CALCULATOR,
              unit: options[key].BASE_UNIT || 'шт',
              weight: options[key].WEIGHT_TONS ? (Math.ceil(parseFloat(String(options[key].WEIGHT_TONS).replace(",", ".")) * 100) / 100).toFixed(2) : '-',
              volume: options[key].VOLUME_CUBIC ? (Math.ceil(parseFloat(String(options[key].VOLUME_CUBIC).replace(",", ".")) * 100) / 100).toFixed(2) : '-',
              factVolume: options[key].PALLET_UNITS ? parseFloat(String(options[key].PALLET_UNITS).replace(",", ".")) : '-',
              numberPallets: options[key].PALLET_UNITS ? 1 : 0,
              bitrixId: options[key].BITRIX_ID || '',
              source: options[key].SOURCE || '-',
              priceValue,
            };
          }).sort((a, b) => sortByPriceAvailability(a, b) || (a.priceValue - b.priceValue) || a.label.localeCompare(b.label));
          setFilteredOptions(uniqueByLabel(allOptions));
          setIsOptionsOpen(true);
          setIsSearching(false);
          return;
        }

        const cacheKey = value + ':' + LIMIT + ':' + paymentMethod;
        if (searchCache.current.has(cacheKey)) {
          setFilteredOptions(uniqueByLabel(searchCache.current.get(cacheKey)));
          setIsOptionsOpen(true);
          setIsSearching(false);
          return;
        }

        setIsSearching(true);
        const normalized = normalizeOsbText(value.toLowerCase());
        const normalizedQuery = normalized.primary || '';
        const searchWords = normalizedQuery.split(/\s+/).filter(w => w.length > 0); // Убрано ограничение на длину
        const transliteratedWords = searchWords.map(word => transliterate(word));
        const latinWords = searchWords.map(word => transliterateToLatin(word));
        const cyrillicWords = searchWords.map(word => transliterateToCyrillic(word));
        // Запрос со склеенными одинаковыми на вид буквами: им ищется маркировка
        // профиля, набранная не в той раскладке.
        const odinakovyeQuery = odinakovyeBukvy(normalizedQuery);
        const odinakovyeWords = searchWords.map((word) => odinakovyeBukvy(word));

        // Извлекаем числовые значения из запроса
        const queryNumbers = normalizedQuery.match(/\d+(\.\d+)?/g) || [];
        const queryNumbersFloat = queryNumbers.map(num => parseFloat(num));

        if (normalizedQuery.includes('бонолит')) {
          console.log('🔍 Search debug для "бонолит":', {
            input: value,
            normalizedQuery,
            searchWords,
            transliteratedWords,
            latinWords,
            cyrillicWords,
            queryNumbersFloat,
            // Дополнительная отладка для транслитерации
            latinToOriginal: transliterateToLatin(normalizedQuery),
            cyrillicToOriginal: transliterateToCyrillic(normalizedQuery),
            // Специальный дебаг для "бонолит"
            isBonolit: normalizedQuery.includes('бонолит'),
            bonolitTranslit: transliterateToLatin('бонолит')
          });
        }

        let filteredKeys = Object.keys(options)
          .filter(key => options[key].CALCULATOR); // Исключаем элементы без категории

        // Применяем ограничения если необходимо
        if (restrictToNonmetallic) {
          filteredKeys = filteredKeys.filter(key => options[key].CALCULATOR === "Нерудные материалы");
        } else if (excludeNonmetallic) {
          filteredKeys = filteredKeys.filter(key => options[key].CALCULATOR !== "Нерудные материалы");
        }

        const filtered = filteredKeys.map(key => {
          const item = options[key];
          const itemName = normalizeOsbText(key.toLowerCase()).primary;
          if (!itemName) return null;
          const transliteratedItemName = transliterate(itemName);
          const latinItemName = transliterateToLatin(itemName);
          const cyrillicItemName = transliterateToCyrillic(itemName);
          const odinakovyeItemName = odinakovyeBukvy(itemName);

          // Проверяем совпадения
          const matchesOriginalWords = searchWords.every(word => itemName.includes(word));

          // Для транслитерированных слов проверяем все варианты
          const matchesTransliteratedWords = transliteratedWords.every(word =>
            itemName.includes(word) || transliteratedItemName.includes(word)
          );

          // Проверяем Latin слова (результат transliterateToLatin от кириллических слов)
          const matchesLatinTransliteration = latinWords.length > 0 && latinWords.every(word =>
            itemName.includes(word) || // прямое совпадение с оригинальным именем элемента
            latinItemName.includes(word) || // совпадение с латинизированной версией имени элемента
            transliteratedItemName.includes(word) || // совпадение с транслитерированной версией
            cyrillicItemName.includes(word) // совпадение с кириллизированной версией имени элемента
          );

          // Проверяем Cyrillic слова (результат transliterateToCyrillic от латинских слов) 
          const matchesCyrillicTransliteration = cyrillicWords.length > 0 && cyrillicWords.every(word =>
            itemName.includes(word) || // прямое совпадение с оригинальным именем элемента
            cyrillicItemName.includes(word) || // совпадение с кириллизированной версией имени элемента
            transliteratedItemName.includes(word) || // совпадение с транслитерированной версией
            latinItemName.includes(word) // совпадение с латинизированной версией имени элемента
          );

          // Совпадение по одинаковым на вид буквам: «С8» кириллицей находит «C8»
          // латиницей и наоборот, а «C20А»-кентавр находится в любой раскладке.
          const matchesOdinakovyeBukvy = odinakovyeWords.length > 0
            && odinakovyeWords.every((word) => odinakovyeItemName.includes(word));

          const matchesFullInput = itemName.includes(normalizedQuery) ||
            transliteratedItemName.includes(normalizedQuery) ||
            latinItemName.includes(normalizedQuery) ||
            cyrillicItemName.includes(normalizedQuery) ||
            // Дополнительная проверка для обратной транслитерации
            itemName.includes(transliterateToCyrillic(normalizedQuery)) ||
            transliterateToCyrillic(itemName).includes(normalizedQuery);

          // Отладка для элементов с "bonolit"
          if (key.toLowerCase().includes('bonolit') && normalizedQuery.includes('бонолит')) {
            console.log('🔍 Bonolit match debug для элемента:', key);
            console.log('🔍 Детали сопоставления:', {
              originalKey: key,
              itemName,
              normalizedQuery,
              searchWords,
              latinWords,
              cyrillicWords,
              matchesOriginalWords,
              matchesTransliteratedWords,
              matchesLatinTransliteration,
              matchesCyrillicTransliteration,
              matchesFullInput,
              // Дополнительная детализация
              latinItemName,
              cyrillicItemName,
              transliteratedItemName,
              // Проверяем каждое слово отдельно
              wordChecks: latinWords.map(word => ({
                word,
                inItemName: itemName.includes(word),
                inLatinItemName: latinItemName.includes(word),
                inTransliteratedItemName: transliteratedItemName.includes(word),
                inCyrillicItemName: cyrillicItemName.includes(word)
              }))
            });
          }

          if (!(matchesOriginalWords || matchesTransliteratedWords || matchesLatinTransliteration || matchesCyrillicTransliteration || matchesFullInput || matchesOdinakovyeBukvy)) {
            return null;
          }

          // Извлекаем числа из имени элемента
          const itemNumbers = itemName.match(/\d+(\.\d+)?/g) || [];
          const itemNumbersFloat = itemNumbers.map(num => parseFloat(num));

          // Рассчитываем релевантность
          let relevance = 0;

          // Точное совпадение всего запроса
          if (itemName === normalizedQuery || transliteratedItemName === normalizedQuery ||
            latinItemName === normalizedQuery || cyrillicItemName === normalizedQuery ||
            odinakovyeItemName === odinakovyeQuery) {
            relevance += 100;
          }
          // Запрос в начале строки
          else if (itemName.startsWith(normalizedQuery) || transliteratedItemName.startsWith(normalizedQuery) ||
            latinItemName.startsWith(normalizedQuery) || cyrillicItemName.startsWith(normalizedQuery) ||
            odinakovyeItemName.startsWith(odinakovyeQuery)) {
            relevance += 50;
          }
          // Полное совпадение запроса где-то в строке
          else if (matchesFullInput) {
            relevance += 30;
          }

          // ВАЖНО: Высокий приоритет за совпадение ВСЕХ слов запроса
          if (matchesOriginalWords) {
            relevance += 20;

            // Дополнительный бонус за последовательное расположение слов
            const queryWords = normalizedQuery.split(/\s+/).filter(w => w.length > 0);
            if (queryWords.length > 1) {
              let consecutiveBonus = 0;
              let prevIndex = -1;
              let allInOrder = true;

              // Проверяем, идут ли все слова по порядку и насколько близко друг к другу
              for (const word of queryWords) {
                const index = itemName.indexOf(word);
                if (index >= 0) {
                  if (prevIndex >= 0) {
                    const distance = index - prevIndex;
                    // Если слова идут подряд (расстояние меньше 20 символов), даем больше баллов
                    if (distance > 0 && distance < 20) {
                      consecutiveBonus += 10;
                    } else if (distance > 0) {
                      consecutiveBonus += 2;
                    } else {
                      allInOrder = false;
                    }
                  }
                  prevIndex = index + word.length;
                } else {
                  allInOrder = false;
                  break;
                }
              }

              if (allInOrder) {
                relevance += consecutiveBonus;
              }
            }
          }
          // Совпадение транслитерированных слов (меньший приоритет)
          else if (matchesTransliteratedWords || matchesLatinTransliteration || matchesCyrillicTransliteration) {
            relevance += 5;
          }

          // Проверка числовых совпадений
          let numberMatchScore = 0;
          if (queryNumbersFloat.length > 0 && itemNumbersFloat.length > 0) {
            queryNumbersFloat.forEach((qNum, qIdx) => {
              if (itemNumbersFloat[qIdx] === qNum) {
                numberMatchScore += 2; // Большой вес за точное совпадение
              } else if (itemNumbersFloat[qIdx]) {
                // Близость чисел
                const diff = Math.abs(itemNumbersFloat[qIdx] - qNum);
                if (diff <= 2) {
                  numberMatchScore += 1 / (diff + 1); // Чем меньше разница, тем выше вес
                }
              }
            });
          }
          relevance += numberMatchScore;

          const priceValue = getPriceForPaymentMethod(item, paymentMethod);

          return {
            value: key,
            label: key,
            category: item.CALCULATOR,
            unit: item.BASE_UNIT || 'шт',
            weight: item.WEIGHT_TONS ? (Math.ceil(parseFloat(String(item.WEIGHT_TONS).replace(",", ".")) * 100) / 100).toFixed(2) : '-',
            volume: item.VOLUME_CUBIC ? (Math.ceil(parseFloat(String(item.VOLUME_CUBIC).replace(",", ".")) * 100) / 100).toFixed(2) : '-',
            factVolume: item.PALLET_UNITS ? parseFloat(String(item.PALLET_UNITS).replace(",", ".")) : '-',
            numberPallets: item.PALLET_UNITS ? 1 : 0,
            bitrixId: item.BITRIX_ID || '',
            source: item.SOURCE || '-',
            relevance,
            numberDiff: numberMatchScore,
            priceValue,
          };
        })
          .filter(item => item !== null)
          .sort((a, b) => {
            const priceAvailabilityOrder = sortByPriceAvailability(a, b);
            if (priceAvailabilityOrder !== 0) {
              return priceAvailabilityOrder;
            }
            if (a.priceValue !== b.priceValue) {
              return a.priceValue - b.priceValue;
            }
            if (b.relevance !== a.relevance) {
              return b.relevance - a.relevance;
            }
            if (b.numberDiff !== a.numberDiff) {
              return b.numberDiff - a.numberDiff;
            }
            return a.label.localeCompare(b.label);
          })
          .slice(0, LIMIT);

        const uniqueFiltered = uniqueByLabel(filtered);
        searchCache.current.set(cacheKey, uniqueFiltered);
        if (searchCache.current.size > 100) {
          searchCache.current.delete(searchCache.current.keys().next().value);
        }

        setFilteredOptions(uniqueFiltered);
        setIsOptionsOpen(true);
        setIsSearching(false);
      }, 500),
      [excludeNonmetallic, options, paymentMethod, restrictToNonmetallic]
    );

    const handleInputChange = useCallback(
      (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        const searchId = Date.now().toString();
        latestSearchRef.current = searchId;
        debouncedSearch(newValue);
      },
      [debouncedSearch]
    );

    const handleSelect = useCallback((option) => {
      console.log(`✅ Выбран продукт:`, option);
      setInputValue(option.value);
      setIsOptionsOpen(false);
      onChange(option.value); // Передаем только value, как в DropdownTable
    }, [onChange]);

    const handleKeyDown = useCallback((e) => {
      if (e.key === "Escape") {
        console.log(`🔙 Escape: сброс на ${selectedValue || ""}`);
        setInputValue(selectedValue || "");
        setIsOptionsOpen(false);
        e.preventDefault();
      }
      if (onKeyDown) onKeyDown(e);
    }, [selectedValue, onKeyDown]);

    const handleMouseDown = useCallback(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        setIsOptionsOpen(true);
        debouncedSearch(inputValue);
      }
    }, [inputValue, debouncedSearch]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOptionsOpen(false);
          setInputValue(selectedValue || ""); // Сбрасываем на selectedValue
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [selectedValue]);

    useEffect(() => {
      setInputValue(selectedValue || '');
    }, [selectedValue]);

    // Раньше тут был auto-resize textarea под scrollHeight. Для компактного
    // вида в таблице держим фиксированную высоту 32px (см. inline styles ниже).

    const updateDropdownPosition = useCallback(() => {
      if (textareaRef.current) {
        const rect = textareaRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
    }, []);

    useEffect(() => {
      if (isOptionsOpen) updateDropdownPosition();
    }, [isOptionsOpen, updateDropdownPosition]);

    return (
      <div
        style={{ minWidth: 0, width: '100%', position: 'relative' }}
        ref={dropdownRef}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div onKeyDown={handleKeyDown} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
            <textarea
              id={id}
              ref={textareaRef}
              value={inputValue}
              onChange={handleInputChange}
              onMouseDown={handleMouseDown}
              placeholder="Выбрать товар..."
              spellCheck={false}
              autoCorrect="off"
              autoCapitalize="off"
              style={{
                minHeight: '32px',
                height: '32px',
                width: '100%',
                border: '1px solid #dbe3ef',
                borderRadius: '8px',
                background: '#ffffff',
                fontFamily: '"Inter", -apple-system, system-ui, "Segoe UI", Roboto, Arial, sans-serif',
                fontSize: '13.5px',
                fontWeight: 600,
                color: '#020217',
                letterSpacing: '-0.005em',
                lineHeight: 1.25,
                padding: '6px 30px 6px 10px',
                resize: 'none',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                boxSizing: 'border-box',
                display: 'block',
                margin: 0,
                verticalAlign: 'middle',
                boxShadow: '0 1px 2px rgba(2,2,23,0.04)',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.15s, box-shadow 0.15s',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#1f7fd1';
                e.currentTarget.style.boxShadow = '0 0 0 2px rgba(31,127,209,0.18)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#dbe3ef';
                e.currentTarget.style.boxShadow = '0 1px 2px rgba(2,2,23,0.04)';
              }}
              rows={1}
            />
            {isSearching && (
              <i
                className="bi bi-arrow-repeat"
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '14px',
                  color: '#7a8aa3',
                  animation: 'spin 1s linear infinite',
                }}
              />
            )}
          </div>
        </div>
        {isOptionsOpen && portalNode && createPortal(
          <ul
            style={{
              position: 'absolute',
              top: dropdownPosition.top + 4,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              maxHeight: '320px',
              overflowY: 'auto',
              zIndex: 9999,
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              background: '#ffffff',
              border: '1px solid rgba(2,2,23,0.08)',
              borderRadius: '12px',
              boxShadow: '0 1px 2px rgba(2,2,23,0.04), 0 20px 40px -18px rgba(2,2,23,0.3)',
              padding: '6px',
              margin: 0,
              listStyle: 'none',
              fontFamily: '"Inter", -apple-system, system-ui, "Segoe UI", Roboto, Arial, sans-serif',
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.slice(0, 25).map((option) => {
                const isPicked = option.value === inputValue;
                return (
                  <li key={option.value} style={{ margin: 0, padding: 0 }}>
                    <button
                      type="button"
                      onMouseDown={() => handleSelect(option)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                        width: '100%',
                        textAlign: 'left',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        padding: '9px 12px',
                        background: isPicked
                          ? 'linear-gradient(90deg, rgba(31,127,209,0.10), transparent 70%)'
                          : 'transparent',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#020217',
                        fontFamily: 'inherit',
                        fontSize: '13.5px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: isPicked ? 'inset 3px 0 0 #005ca9' : 'none',
                        transition: 'background-color 0.12s',
                      }}
                      onMouseEnter={(e) => {
                        if (!isPicked) e.currentTarget.style.background = '#f6faff';
                      }}
                      onMouseLeave={(e) => {
                        if (!isPicked) e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <span style={{ letterSpacing: '-0.005em', color: isPicked ? '#005ca9' : '#020217' }}>
                        {highlightMatch(option.label, inputValue)}
                      </span>
                      {showMinPrice(option)}
                    </button>
                  </li>
                );
              })
            ) : (
              <li style={{
                padding: '14px 12px',
                textAlign: 'center',
                fontSize: '13px',
                fontWeight: 500,
                color: '#7a8aa3',
              }}>
                Ничего не нашлось
              </li>
            )}
          </ul>,
          portalNode
        )}
      </div>
    );
  }
);

export default React.memo(ProductSearch);
